import { FC } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { client, createCacheKey } from '../lib/sanityClient';

interface Artist {
  _id: string;
  name: string;
  slug: string;
}

interface ArtistNavProps {
  hideOnMenu?: boolean;
}

const ArtistNav: FC<ArtistNavProps> = ({ hideOnMenu }) => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Only show on artist pages and not when menu is open
  const isArtistPage = location.pathname.startsWith('/artist/');
  if (!isArtistPage || hideOnMenu) {
    return null;
  }

  const { data: artists, isLoading, error } = useQuery<Artist[]>({
    queryKey: createCacheKey('all-artists'),
    queryFn: async () => {
      const query = `*[_type == "artist"] | order(name asc) {
        _id,
        name,
        "slug": slug.current
      }`;
      return client.fetch<Artist[]>(query);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false
  });
  
  if (isLoading || error || !artists) {
    return null;
  }

  // Function to handle navigation
  const handleArtistClick = (artistSlug: string) => {
    navigate(`/artist/${artistSlug}`);
  };

  return (
    <div className="artist-nav-container relative flex justify-center gap-4 mb-6 pt-2 z-50 overflow-x-auto hide-scrollbar">
      {artists.map((artist) => (
        <button
          key={artist._id}
          type="button"
          onClick={() => handleArtistClick(artist.slug)}
          className={`relative text-base px-4 py-2 cursor-pointer ${artist.slug === slug ? 'font-medium border-b-2 border-black' : 'text-gray-500 hover:text-gray-800'}`}
          style={{ pointerEvents: 'auto' }}
        >
          {artist.name}
        </button>
      ))}
    </div>
  );
};

export default ArtistNav;
